/**
 * Bid-agent "Sources" selection helpers (MYT-1727).
 *
 * The bid agent (io.ai) lets users select / deselect which sources it may use:
 * the content library, the internet (external research), and the tender library.
 * The chosen set travels to the backend as `selected_tools` on the run context,
 * where each source's tool honours it via `tool_enabled` (see the backend
 * `agents_v2/tools/context.py`).
 *
 * Architecture: this module holds only the *pure* selection logic (the constant
 * default set and the run-context normaliser). The stateful wiring - holding the
 * selection per thread and rendering the {@link ToolSelector} control - lives in
 * `bid-agent-sidebar.tsx` / `bid-agent-composer.tsx`. Keeping the decision pure
 * here makes it unit-testable without a live chat runtime and mirrors the
 * library-chat `contextForRun` convention of omitting `selected_tools` when the
 * full default set is selected (so the backend applies its own defaults for
 * legacy / unscoped runs).
 */

// ========== SOURCE IDS ==========

/** Content library search source id. */
export const CONTENT_LIBRARY_TOOL = "content_library_search";
/** Internet / external research search source id. */
export const WEB_SEARCH_TOOL = "external_research_search";
/** Tender library / tender document search source id. */
export const TENDER_LIBRARY_TOOL = "tender_library_search";

/**
 * The sources enabled by default for the bid agent: library, internet, tender.
 * Kept in sync with the backend `DEFAULT_ENABLED_TOOLS`, so an unscoped run has
 * every source available (the agent's historical behaviour).
 */
export const BID_AGENT_DEFAULT_TOOLS: string[] = [
  CONTENT_LIBRARY_TOOL,
  WEB_SEARCH_TOOL,
  TENDER_LIBRARY_TOOL
];

/**
 * Whether the given selection is exactly the full default set (order-insensitive).
 * @param selectedTools - The currently selected source ids.
 * @returns True when every default source (and no fewer) is selected.
 */
const isFullDefaultSelection = (selectedTools: string[]): boolean =>
  selectedTools.length === BID_AGENT_DEFAULT_TOOLS.length &&
  BID_AGENT_DEFAULT_TOOLS.every((tool) => selectedTools.includes(tool));

/**
 * Normalise a source selection into the value that should ride on the run
 * context's `selected_tools`. Returns `undefined` when the full default set is
 * selected (so the backend applies its defaults), otherwise the explicit list -
 * including an empty list when the user has deselected every source.
 * @param selectedTools - The currently selected source ids.
 * @returns The `selected_tools` value for the run context, or `undefined`.
 * @example
 * bidAgentRunContextSelectedTools(["content_library_search"]) // ["content_library_search"]
 * bidAgentRunContextSelectedTools([...BID_AGENT_DEFAULT_TOOLS]) // undefined
 */
export const bidAgentRunContextSelectedTools = (
  selectedTools: string[]
): string[] | undefined =>
  isFullDefaultSelection(selectedTools) ? undefined : selectedTools;
